import { useState, useCallback, useRef } from "react";
import type { LevelCheckpoint } from "./targetCompression";
import { compressPdf, getPdfPageCount, type CompressLimit, type CompressMode, type CompressResult } from "./operations";
import { whyNotReached, stopNoteSentence, type StopNote } from "./compressLadder";
import { CompressStopped } from "./imageRecompress";
import { FileDropZone, ProcessingState, DownloadButton } from "@/components/ToolLayout";
import { ToolWithSidebar } from "@/components/ToolWithSidebar";
import { PdfBeforeAfter, PdfPageThumb } from "@/components/PdfBeforeAfter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { NoticeBox } from "@/components/NoticeBox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { pdfBytesToBlob } from "./utils";
import { usePipelineContext } from "@/lib/pipelineContext";
import { trackToolCompleted } from "@/lib/analytics";
import { useRunToken } from "@/hooks/useRunToken";
import { formatFileSize } from "@/lib/formatFileSize";
import { plural } from "@/lib/plural";
import { stemOfPdfName } from "./pdfMetadata";
import { AlertTriangle, Check, Info, Loader2, Target } from "lucide-react";
import { BatchRunner, BatchModeToggle } from "@/components/BatchRunner";
import { pdfWeight, weightAdvice, weightRows, type PdfWeight } from "./pdfWeight";

const TOOL_INFO_CONTENT = {
  what: `Makes a PDF smaller. There are four ways to do it:\n\n• Balanced tidies the file's structure, empties blank pages and shrinks the pictures inside, first to about 140 dots per inch and then picture by picture, as far as each can go. Text stays selectable, and links, bookmarks and form fields are kept. The last level can soften fine detail in photos, so pick an earlier level if it looks too soft.\n• Aggressive does everything Balanced does, then turns pages into pictures in finer and finer steps (empty areas dropped, flat areas stored as plain fills, photos and text at lower resolutions). Text on those pages is no longer selectable.\n• Extreme does everything Aggressive does, then goes further: every page becomes grey at 70 dots per inch, then pure black and white. Colour is lost.\n• Target size shrinks step by step until your limit is met, such as "under 2 MB" for an email or a portal. It stops as soon as the file fits, and if it can't fit, it tells you the final size and why.\n\nCompression runs in levels, gentlest first. Every finished level is a complete file you can download from the same list, so you choose the one that looks right. Compare the original with any level page by page before downloading. To shrink several PDFs with the same setting, switch to Several files (up to 5 at a time on the trial, counted as one use).`,
  tip: `The size depends on what is inside the file. A scan or a PDF full of photos can shrink a lot; a text-only PDF is already small and usually can't. If a result is not smaller, the tool tells you and you keep your original.`,
};

type Mode = CompressMode;

const MODE_LABELS: Partial<Record<Mode, string>> = {
  balanced: "Balanced",
  aggressive: "Aggressive",
  extreme: "Extreme",
  size: "Target size",
};
const MODES: Mode[] = ["balanced", "aggressive", "extreme", "size"];

const MODE_HELP: Record<string, string> = {
  balanced: "Tidies the file, empties blank pages, and shrinks the pictures inside, first to about 140 dots per inch and then picture by picture, as far as each can go. Text stays selectable; links, bookmarks, form fields and page sizes are kept. The last level can soften fine detail in photos; every level is its own file, so you can pick an earlier one.",
  aggressive: "Everything in Balanced, then pages are turned into pictures in finer and finer steps: empty areas dropped, flat areas stored as plain fills, photos, text and small print at lower resolutions. Text on those pages is no longer selectable, and links and form fields on them are gone.",
  extreme: "Everything in Aggressive, then every page is converted to grey at 70 dots per inch, and finally to pure black and white. Colour and shading are lost and text is no longer selectable. Use it only when nothing gentler is small enough.",
  size: "Set the size you need. Steps run gentlest first and stop as soon as the file fits; if it can't fit, every step is tried and you are told the final size and why.",
};

const LIMIT_LABELS: Record<CompressLimit, string> = {
  balanced: "Balanced steps only (text stays selectable)",
  aggressive: "Up to Aggressive (pages may become pictures)",
  extreme: "All the way to Extreme (greyscale, black and white)",
};

/** The next, stronger mode to offer when a mode could not make the file smaller. */
const NEXT_MODE: Partial<Record<Mode, Mode>> = { balanced: "aggressive", aggressive: "extreme" };
const NEXT_MODE_NOTE: Record<string, string> = {
  aggressive: "also turns pages into pictures, finer and finer. Text on those pages is no longer selectable.",
  extreme: "also makes every page grey at a low resolution, then pure black and white. Colour is lost.",
};

// Presets in KB, filtered at render time to the ones below the file's own size.
const TARGET_PRESETS = [
  { label: "500 KB", bytes: 500 * 1024 },
  { label: "1 MB", bytes: 1 * 1024 * 1024 },
  { label: "2 MB", bytes: 2 * 1024 * 1024 },
  { label: "5 MB", bytes: 5 * 1024 * 1024 },
  { label: "10 MB", bytes: 10 * 1024 * 1024 },
];

type Shown =
  | { kind: "ok"; blob: Blob; size: number; method: "structure" | "images" | "pictures"; metTarget?: boolean; targetBytes?: number; imagesShrunk: number; imageCount: number; pictureFallbackWouldHelp?: boolean; pictureFallbackTried?: boolean; pagesConverted?: number; pagesKept?: number; stoppedAtLevel?: LevelCheckpoint; stopNote?: StopNote }
  | { kind: "unchanged"; reason: "no-gain" | "no-images" | "unsupported-pictures" | "under-target"; imageCount: number; targetBytes?: number; pictureFallbackWouldHelp?: boolean; pictureFallbackTried?: boolean };

/** Several PDFs, one setting. A file that cannot be made smaller is returned as it was. */
function CompressBatch() {
  const [mode, setMode] = useState<Mode>("balanced");
  const [limit, setLimit] = useState<CompressLimit>("aggressive");
  const [targetInput, setTargetInput] = useState("2");
  const [targetUnit, setTargetUnit] = useState<"MB" | "KB">("MB");
  const targetBytes = () => Math.round(parseFloat(targetInput || "0") * (targetUnit === "MB" ? 1024 * 1024 : 1024));
  return (
    <BatchRunner
      accept=".pdf" noun="PDF" zipName="compressed-pdfs.zip" runLabel="Compress all"
      check={() => (mode === "size" && !(targetBytes() > 0) ? "Enter a valid target size" : null)}
      process={async (f) => {
        const out = await compressPdf(f, mode, { targetBytes: mode === "size" ? targetBytes() : undefined, limit });
        if (out.status === "unchanged") return { blob: f, fileName: f.name };
        return { blob: pdfBytesToBlob(out.bytes), fileName: `${stemOfPdfName(f.name)}-compressed.pdf` };
      }}
    >
      <div className="space-y-3">
        <div className="flex gap-2 flex-wrap" role="group" aria-label="Compression mode">
          {MODES.map((m) => (
            <Button key={m} size="sm" variant={mode === m ? "default" : "outline"} aria-pressed={mode === m} onClick={() => setMode(m)}>
              {m === "size" && <Target className="h-3.5 w-3.5 mr-1.5" />}
              {MODE_LABELS[m]}
            </Button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">{MODE_HELP[mode]}</p>
        {mode === "size" && (
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <Label htmlFor="cb-target" className="text-xs">Target size for each file</Label>
              <div className="flex gap-2">
                <Input id="cb-target" type="number" min="0" step="0.1" value={targetInput} onChange={(e) => setTargetInput(e.target.value)} className="w-24" />
                <select value={targetUnit} aria-label="Target size unit" onChange={(e) => setTargetUnit(e.target.value as "MB" | "KB")} className="border rounded-md px-2 text-sm bg-background"><option value="MB">MB</option><option value="KB">KB</option></select>
              </div>
            </div>
            <div>
              <Label htmlFor="cb-limit" className="text-xs">Go as far as</Label>
              <select id="cb-limit" value={limit} onChange={(e) => setLimit(e.target.value as CompressLimit)} className="border rounded-md px-2 py-2 text-sm bg-background block">
                {(Object.keys(LIMIT_LABELS) as CompressLimit[]).map((l) => <option key={l} value={l}>{LIMIT_LABELS[l]}</option>)}
              </select>
            </div>
          </div>
        )}
        <p className="text-xs text-muted-foreground">Each file gets the smallest result for this setting. Files that cannot be made smaller are returned as they were.</p>
      </div>
    </BatchRunner>
  );
}

export default function CompressPdf() {
  const pipeline = usePipelineContext();
  const inPipeline = pipeline !== null;
  const [file, setFile] = useState<File | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [mode, setMode] = useState<Mode>("balanced");
  const [limit, setLimit] = useState<CompressLimit>("extreme");
  const [targetInput, setTargetInput] = useState("2");
  const [targetUnit, setTargetUnit] = useState<"MB" | "KB">("MB");
  const [processing, setProcessing] = useState(false);
  const [progressMsg, setProgressMsg] = useState("");
  const [shown, setShown] = useState<Shown | null>(null);
  const [batchMode, setBatchMode] = useState(false);
  const [weight, setWeight] = useState<PdfWeight | null>(null);
  const [levels, setLevels] = useState<LevelCheckpoint[]>([]);
  const lastLevel = useRef<LevelCheckpoint | null>(null);
  const runs = useRunToken();

  const parsedTarget = () => Math.round(parseFloat(targetInput || "0") * (targetUnit === "MB" ? 1024 * 1024 : 1024));

  // Anything that changes what a run would do makes the result on screen stale, and ends a run in progress.
  const invalidate = useCallback(() => {
    runs.cancel();
    setProcessing(false);
    setProgressMsg("");
    setShown(null);
    setLevels([]);
    lastLevel.current = null;
  }, [runs]);

  const handleFiles = useCallback(async (files: File[]) => {
    const f = files[0];
    if (!f) return;
    invalidate();
    setMode("balanced");
    setFile(f);
    setPageCount(0);
    setWeight(null);
    // Where the bytes go, so the person can see which mode will help. Skipped for very large files (it reads the whole file).
    if (f.size <= 60 * 1024 * 1024) f.arrayBuffer().then((b) => pdfWeight(b)).then((w) => setWeight(w)).catch(() => undefined);
    try {
      setPageCount(await getPdfPageCount(f));
    } catch {
      /* the page count is a nicety; compression reports its own problems */
    }
  }, [invalidate]);

  const reset = () => {
    invalidate();
    setFile(null);
    setPageCount(0);
    setWeight(null);
    setMode("balanced"); // a mode that changes how the PDF is built is never preselected for the next file
    setTargetInput("2");
    setTargetUnit("MB");
  };

  /** Stopping keeps the last level that finished: it is a complete file, ready to download. */
  const handleStop = () => {
    const last = lastLevel.current;
    if (!last || !file) {
      invalidate();
      toast.info("Stopped.");
      return;
    }
    runs.cancel();
    setProcessing(false);
    setProgressMsg("");
    setShown({
      kind: "ok", blob: pdfBytesToBlob(last.bytes), size: last.size, method: last.turnsPagesToPictures ? "pictures" : last.name.startsWith("Tidy") ? "structure" : "images",
      imagesShrunk: 0, imageCount: 0, pagesConverted: last.pagesConverted, pagesKept: last.pagesKept, stoppedAtLevel: last,
    });
    toast.info(`Stopped. Kept level ${last.level}: ${formatFileSize(last.size)}`);
  };

  const run = async (modeOverride?: Mode, runAllLevels?: boolean) => {
    if (!file) return;
    runs.cancel();
    setShown(null);
    const runMode: Mode = modeOverride ?? mode;
    if (modeOverride) setMode(modeOverride);
    let targetBytes: number | undefined;
    if (runMode === "size") {
      targetBytes = parsedTarget();
      if (!targetBytes || targetBytes <= 0) {
        toast.error("Enter a valid target size");
        return;
      }
    }
    const me = runs.start();
    setProcessing(true);
    setProgressMsg("");
    setLevels([]);
    lastLevel.current = null;
    try {
      const outcome: CompressResult = await compressPdf(file, runMode, {
        targetBytes,
        limit,
        runAllLevels,
        onProgress: (m) => runs.isCurrent(me) && setProgressMsg(m),
        shouldStop: () => !runs.isCurrent(me),
        onLevel: (cp) => {
          if (!runs.isCurrent(me)) return;
          lastLevel.current = cp;
          setLevels((prev) => [...prev, cp]);
        },
      });
      if (!runs.isCurrent(me)) return; // stopped or replaced while this was in flight -- discard silently

      if (outcome.status === "unchanged") {
        setShown({ kind: "unchanged", reason: outcome.reason, imageCount: outcome.imageCount, targetBytes, pictureFallbackWouldHelp: outcome.pictureFallbackWouldHelp, pictureFallbackTried: outcome.pictureFallbackTried });
        return;
      }
      setShown({
        kind: "ok", blob: pdfBytesToBlob(outcome.bytes), size: outcome.size, method: outcome.method, metTarget: outcome.metTarget,
        targetBytes: outcome.targetBytes, imagesShrunk: outcome.imagesShrunk, imageCount: outcome.imageCount,
        pictureFallbackWouldHelp: outcome.pictureFallbackWouldHelp, pictureFallbackTried: outcome.pictureFallbackTried,
        pagesConverted: outcome.pagesConverted, pagesKept: outcome.pagesKept, stopNote: outcome.stopNote,
      });
      trackToolCompleted("compress-pdf", "pdf-tools");
      if (runMode === "size" && outcome.metTarget === false) {
        toast.warning("Couldn't reach the target — here's the smallest result");
      } else {
        toast.success(`Compressed: ${formatFileSize(file.size)} → ${formatFileSize(outcome.size)}`);
      }
    } catch (err) {
      if (err instanceof CompressStopped || !runs.isCurrent(me)) return;
      console.error("Compression failed:", err);
      toast.error("Failed to compress PDF");
    } finally {
      if (runs.isCurrent(me)) {
        setProcessing(false);
        setProgressMsg("");
      }
    }
  };

  /** Puts a finished level into the before/after viewer and makes it the file the main download button gives. */
  const showLevel = (cp: LevelCheckpoint) => {
    setShown({
      kind: "ok", blob: pdfBytesToBlob(cp.bytes), size: cp.size, method: cp.turnsPagesToPictures ? "pictures" : cp.name.startsWith("Tidy") ? "structure" : "images",
      imagesShrunk: 0, imageCount: 0, pagesConverted: cp.pagesConverted, pagesKept: cp.pagesKept, stoppedAtLevel: cp,
    });
  };

  // The same plain-words check before anything runs: a target at or above the file's size needs no work.
  const target = parsedTarget();
  const alreadyUnderTarget = !!file && mode === "size" && target > 0 && file.size <= target;

  const presets = file ? TARGET_PRESETS.filter((p) => p.bytes < file.size) : TARGET_PRESETS;

  return (
    <ToolWithSidebar toolId="compress-pdf" categoryId="pdf-tools" whatItDoes={TOOL_INFO_CONTENT.what} proTip={TOOL_INFO_CONTENT.tip}>
      <BatchModeToggle batch={batchMode} onChange={setBatchMode} />
      {batchMode ? (
        <CompressBatch />
      ) : !file ? (
        <FileDropZone accept=".pdf" onFiles={handleFiles} label="Drop a PDF to compress, or click to browse" description="Shrink the pictures inside, or fit a size limit" gateAtDownload />
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border bg-card flex justify-between items-center gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <PdfPageThumb file={file} />
              <div className="min-w-0">
              <p className="text-sm font-medium truncate">{file.name}</p>
              <p className="text-xs text-muted-foreground">
                Original: {formatFileSize(file.size)}{pageCount > 0 && ` · ${plural(pageCount, "page")}`}
              </p>
              </div>
            </div>
            {shown?.kind === "ok" && <p className="text-sm font-medium text-green-600 shrink-0">Compressed: {formatFileSize(shown.size)}</p>}
          </div>

          {weight && weight.total > 0 && (
            <Card className="p-3 border-border bg-muted/40" data-testid="pdf-weight">
              <p className="text-xs font-medium mb-1.5">What is making this file big</p>
              <ul className="space-y-1">
                {weightRows(weight).map((r) => (
                  <li key={r.label} className="flex items-center gap-2 text-xs">
                    <span className="w-28 shrink-0">{r.label}</span>
                    <span className="flex-1 h-1.5 rounded bg-muted overflow-hidden"><span className="block h-full bg-primary" style={{ width: `${Math.max(2, r.share)}%` }} /></span>
                    <span className="w-40 text-right text-muted-foreground">{formatFileSize(r.bytes)} ({r.share}%){r.note ? `, ${r.note}` : ""}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground mt-2" data-testid="pdf-weight-advice">{weightAdvice(weight)}</p>
            </Card>
          )}

          <div className="flex gap-2 flex-wrap" role="group" aria-label="Compression mode">
            {MODES.map((m) => (
              <Button key={m} size="sm" variant={mode === m ? "default" : "outline"} aria-pressed={mode === m} onClick={() => { if (m !== mode) { invalidate(); setMode(m); } }}>
                {m === "size" && <Target className="h-3.5 w-3.5 mr-1.5" />}
                {MODE_LABELS[m]}
              </Button>
            ))}
          </div>

          <NoticeBox testId="compress-mode-help">
            <p className="text-xs" data-testid="compress-mode-text">{MODE_HELP[mode]}</p>
          </NoticeBox>

          {mode === "size" && (
            <div className="space-y-3">
              <div className="flex items-end gap-2 flex-wrap">
                <div>
                  <Label htmlFor="compress-target" className="text-xs">Target size</Label>
                  <div className="flex gap-2">
                    <Input id="compress-target" type="number" min="0" step="0.1" value={targetInput} onChange={(e) => { invalidate(); setTargetInput(e.target.value); }} className="w-24" />
                    <select
                      value={targetUnit}
                      aria-label="Target size unit"
                      onChange={(e) => { invalidate(); setTargetUnit(e.target.value as "MB" | "KB"); }}
                      className="border rounded-md px-2 text-sm bg-background"
                    >
                      <option value="MB">MB</option>
                      <option value="KB">KB</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="compress-limit" className="text-xs">Go as far as</Label>
                  <select
                    id="compress-limit"
                    value={limit}
                    onChange={(e) => { invalidate(); setLimit(e.target.value as CompressLimit); }}
                    className="border rounded-md px-2 py-2 text-sm bg-background block"
                  >
                    {(Object.keys(LIMIT_LABELS) as CompressLimit[]).map((l) => <option key={l} value={l}>{LIMIT_LABELS[l]}</option>)}
                  </select>
                </div>
                <div className="flex gap-1.5 flex-wrap pb-0.5">
                  {presets.map((p) => (
                    <Button
                      key={p.label}
                      size="sm"
                      variant="ghost"
                      className="h-8 text-xs"
                      onClick={() => {
                        invalidate();
                        setTargetInput(String(p.bytes / (p.bytes >= 1024 * 1024 ? 1024 * 1024 : 1024)));
                        setTargetUnit(p.bytes >= 1024 * 1024 ? "MB" : "KB");
                      }}
                    >
                      {p.label}
                    </Button>
                  ))}
                </div>
              </div>
              {alreadyUnderTarget && <p className="text-xs text-muted-foreground">Your file is {formatFileSize(file.size)}, already under {formatFileSize(target)}. No compression needed.</p>}
            </div>
          )}

          {shown?.kind === "unchanged" && (
            <Card className="p-3 border-border bg-muted/40">
              <div className="flex gap-2">
                <Info className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                <div className="text-sm space-y-1">
                  {shown.reason === "under-target" && <p>Your file is {formatFileSize(file.size)}, already under {formatFileSize(shown.targetBytes ?? 0)}. No compression needed — your original is fine as it is.</p>}
                  {shown.reason === "no-images" && <p>This PDF is mostly text, and its structure is already tight: tidying it saved under 1%. Text can't be squeezed further without changing the file, so nothing was changed.</p>}
                  {shown.reason === "unsupported-pictures" && <p>This PDF is a scan that is already compressed: its pages are stored as scanner pictures (JBIG2 or JPEG 2000). The gentle steps can't shrink those further without changing how the pages look, so nothing was changed.</p>}
                  {shown.reason === "no-gain" && <p>This setting couldn't make the file noticeably smaller (it saved under 1%), because its pictures are already small. Nothing was changed.</p>}
                  {inPipeline && (
                    <div className="pt-1" data-testid="pass-through">
                      <p className="mb-2">Nothing changed, so your file passes through as it is.</p>
                      <DownloadButton blob={file} fileName={file.name} label={pipeline && pipeline.currentStepIndex >= pipeline.steps.length - 1 ? "Download final file" : "Download file"} />
                    </div>
                  )}
                  {shown.reason !== "under-target" && NEXT_MODE[mode] && (
                    <div className="space-y-2 pt-1" data-testid="convert-option">
                      <p>You can still go further: <strong>{MODE_LABELS[NEXT_MODE[mode]!]}</strong> {NEXT_MODE_NOTE[NEXT_MODE[mode]!]}</p>
                      <Button size="sm" onClick={() => void run(NEXT_MODE[mode])} disabled={processing}>
                        Try {MODE_LABELS[NEXT_MODE[mode]!]}
                      </Button>
                    </div>
                  )}
                  {shown.reason !== "under-target" && !NEXT_MODE[mode] && shown.pictureFallbackTried && (
                    <p>Every step was tried, and none made this file noticeably smaller. It is already close to the smallest it can be. To share it, splitting it into parts is the other way to fit a limit.</p>
                  )}
                </div>
              </div>
            </Card>
          )}

          {shown?.kind === "ok" && (
            <NoticeBox testId="compress-result">
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  {formatFileSize(file.size)} → {formatFileSize(shown.size)} ({savedPercent(shown.size, file.size)} smaller)
                  {mode === "size" && shown.targetBytes && (shown.metTarget ? `, under your ${formatFileSize(shown.targetBytes)} target.` : `. Couldn't reach your ${formatFileSize(shown.targetBytes)} target.`)}
                </p>
                {shown.stoppedAtLevel && (
                  <p className="text-xs" data-testid="stopped-note">
                    Showing level {shown.stoppedAtLevel.level} ({shown.stoppedAtLevel.name}). {shown.stoppedAtLevel.detail}
                  </p>
                )}
                <p className="text-xs">
                  {!shown.stoppedAtLevel && shown.method === "images" && `${plural(shown.imagesShrunk, "picture")} shrunk (of ${shown.imageCount}). Text is still selectable; links, bookmarks and form fields are untouched.`}
                  {!shown.stoppedAtLevel && shown.method === "structure" && "Only the file's structure was tidied; nothing you can see changed."}
                  {!shown.stoppedAtLevel && shown.method === "pictures" && "Pages were turned into pictures, so text on those pages is no longer selectable or searchable, and links and form fields on them are gone. Page sizes are kept."}
                </p>
                {shown.stopNote && (
                  <div className="space-y-2" data-testid="stop-note">
                    <p className="text-xs">{stopNoteSentence(shown.stopNote)}</p>
                    {shown.stopNote.kind === "already-small" && (
                      <Button size="sm" variant="outline" onClick={() => void run(mode, true)} disabled={processing} data-testid="run-anyway">
                        Run anyway
                      </Button>
                    )}
                  </div>
                )}
                {mode === "size" && shown.metTarget === false && (
                  <div className="flex gap-2" data-testid="not-reached">
                    <AlertTriangle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <p className="text-xs">{whyNotReached(shown.size, shown.targetBytes ?? 0, limit, pageCount)}</p>
                  </div>
                )}
              </div>
            </NoticeBox>
          )}

          {levels.length > 0 && (
            <Card className="p-3 border-border bg-muted/40" data-testid="compress-levels">
              <p className="text-xs font-medium mb-0.5">Every level is a complete file</p>
              <p className="text-xs text-muted-foreground mb-2">Download any of them, or compare one with your original below.</p>
              <ol className="space-y-2">
                {levels.map((cp) => (
                  <li key={cp.level} className="text-xs" data-testid="compress-level">
                    <div className="flex items-start gap-2">
                      <Check className="h-3.5 w-3.5 text-green-600 shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <span className="font-medium">Level {cp.level}: {cp.name}</span>
                        <span className="text-muted-foreground"> — {formatFileSize(cp.size)} ({savedPercent(cp.size, file.size)} smaller)</span>
                        {cp.turnsPagesToPictures ? <span className="text-muted-foreground"> · pages are pictures, text not selectable</span> : <span className="text-muted-foreground"> · text still selectable</span>}
                        <div className="text-muted-foreground">{cp.detail}</div>
                      </div>
                      {!processing && (
                        <div className="flex gap-1.5 shrink-0">
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => showLevel(cp)}>Compare</Button>
                          <DownloadButton blob={pdfBytesToBlob(cp.bytes)} fileName={`${stemOfPdfName(file.name)}-level-${cp.level}.pdf`} label="Download" />
                        </div>
                      )}
                    </div>
                  </li>
                ))}
                {processing && (
                  <li className="flex items-start gap-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0 mt-0.5" />
                    <span>{progressMsg || "Working on the next level..."}</span>
                  </li>
                )}
              </ol>
              {processing && <p className="text-xs text-muted-foreground mt-2">Stop at any time to keep the last finished level.</p>}
            </Card>
          )}

          <div className="flex gap-3">
            {processing ? <ProcessingState message={progressMsg || "Compressing..."} onStop={handleStop} /> : shown?.kind === "ok" ? (
              <><DownloadButton blob={shown.blob} fileName={`${stemOfPdfName(file.name)}-compressed.pdf`} label="Download Compressed PDF" /><Button variant="outline" onClick={reset}>New File</Button></>
            ) : (
              <><Button onClick={() => void run()} disabled={alreadyUnderTarget} className="btn-glow">Compress PDF</Button><Button variant="outline" onClick={reset}>Reset</Button></>
            )}
          </div>
          {shown?.kind === "ok" && <PdfBeforeAfter key={shown.size} original={file} result={shown.blob} pageCountHint={pageCount || undefined} leftTitle="Original" rightTitle="Compressed" />}
        </div>
      )}
    </ToolWithSidebar>
  );
}

/** "86%", and one decimal above 99 so 99.7% is not shown as "100% smaller". */
function savedPercent(size: number, original: number): string {
  const p = (1 - size / original) * 100;
  if (p >= 99) return `${Math.min(99.9, Math.floor(p * 10) / 10)}%`;
  return `${Math.round(p)}%`;
}
